import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { formatBrandMoney, getBrandDefaultCurrency, getBrandCurrencySuffix, getBrandLocale } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import ReactDOM from 'react-dom';
import { logger } from '../../shared/utils/logger';
import CustomDatePicker from '../../shared/components/custom/CustomDatePicker';
import '../../shared/styles/GPSTrackingDatePicker.css';
import {
  getLiasseFiscale,
  saveManualTableau,
  exportLiasse,
} from './15-liasse-fiscale/liasse-fiscale';
import {
  getCurrentFiscalYear,
} from '../../shared/utils/comptabiliteService';
import { useComptabiliteFiscalYearOptions } from './utils/useComptabiliteFiscalYearOptions';
import { TVCPManager } from './DeclarationsFiscalesPage';
import {
  FileText,
  X,
  Download,
  CheckCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  FileSpreadsheet,
  Calculator,
  Grid,
  TrendingUp,
  TrendingDown,
  Send,
  FileCheck,
  Edit,
  Trash2,
  Upload,
  Users,
  Search,
  Save,
  Plus,
  ChevronDown,
  ChevronUp,
  Shield,
  LayoutGrid,
  BarChart3,
  AlertCircle,
  Eye,
  Settings,
  Calendar,
  Filter,
  XCircle,
} from 'lucide-react';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';

const LIASSE_I18N_NS = 'modules.comptabilite.pages.liasseFiscalePage';

const useLiasseP = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`${LIASSE_I18N_NS}.${key}`, opts);
  return { p, t, i18n };
};

const STATUS_LABEL_KEYS = {
  completed: 'status_completed',
  in_progress: 'status_in_progress',
  pending: 'status_pending',
};

const OVERVIEW_STATUS_LEGACY_MAP = {
  'En préparation': 'overview_status_preparation',
  'En preparation': 'overview_status_preparation',
  preparation: 'overview_status_preparation',
};

const normalizeOverviewStatusKey = (overview = {}) => {
  if (overview.statusKey) return overview.statusKey;
  if (overview.status && OVERVIEW_STATUS_LEGACY_MAP[overview.status]) {
    return OVERVIEW_STATUS_LEGACY_MAP[overview.status];
  }
  return 'overview_status_preparation';
};

const TABLEAU_TITLE_KEYS = {
  '01': 'tableau_01_title', '02': 'tableau_02_title', '03': 'tableau_03_title',
  '04': 'tableau_04_title', '05': 'tableau_05_title', '06': 'tableau_06_title',
  '07': 'tableau_07_title', '08': 'tableau_08_title', '09': 'tableau_09_title',
  '10': 'tableau_10_title', '11': 'tableau_11_title', '12': 'tableau_12_title',
  '13': 'tableau_13_title',
};

const TABLEAU_DESC_KEYS = {
  '01': 'tableau_01_desc', '02': 'tableau_02_desc', '03': 'tableau_03_desc',
  '04': 'tableau_04_desc', '05': 'tableau_05_desc', '06': 'tableau_06_desc',
  '07': 'tableau_07_desc', '08': 'tableau_08_desc', '09': 'tableau_09_desc',
  '10': 'tableau_10_desc', '11': 'tableau_11_desc', '12': 'tableau_12_desc',
  '13': 'tableau_13_desc',
};

const REINT_LABEL_KEYS = {
  R01: 'reint_r01', R02: 'reint_r02', R03: 'reint_r03', R04: 'reint_r04',
  R05: 'reint_r05', R06: 'reint_r06', R07: 'reint_r07', R08: 'reint_r08',
  R09: 'reint_r09', R10: 'reint_r10', R11: 'reint_r11', R12: 'reint_r12', R99: 'reint_r99',
};

const DED_LABEL_KEYS = {
  D01: 'ded_d01', D02: 'ded_d02', D03: 'ded_d03', D04: 'ded_d04',
  D05: 'ded_d05', D06: 'ded_d06', D07: 'ded_d07', D08: 'ded_d08', D99: 'ded_d99',
};

const MAIN_TABS = [
  { id: 'tableaux', labelKey: 'tab_tableaux', icon: LayoutGrid },
  { id: 'redressements', labelKey: 'tab_redressements', icon: AlertTriangle },
  { id: 'synthese', labelKey: 'tab_synthese', icon: BarChart3 },
];

const ADVANCED_MAIN_TABS = [
  { id: 'general', nameKey: 'tab_general', icon: LayoutGrid },
  { id: 'by_status', nameKey: 'tab_by_status', icon: CheckCircle },
  { id: 'by_type', nameKey: 'tab_by_type', icon: Calculator },
];

const QUICK_FILTER_BUTTONS = [
  { id: 'all', nameKey: 'filter_all', icon: LayoutGrid },
  { id: 'auto', nameKey: 'filter_auto', icon: RefreshCw },
  { id: 'manual', nameKey: 'filter_manual', icon: Edit },
  { id: 'completed', nameKey: 'filter_completed', icon: CheckCircle },
  { id: 'in_progress', nameKey: 'filter_in_progress', icon: Clock },
  { id: 'pending', nameKey: 'filter_pending', icon: AlertTriangle },
];

const MGR_TABLEAU_NAME_KEYS = {
  ACTIF: 'mgr_actif', PASSIF: 'mgr_passif', TCR: 'mgr_tcr', TVCP: 'mgr_tvcp',
  '01': 'mgr_t01', '02': 'mgr_t02', '03': 'mgr_t03', '04': 'mgr_t04',
  '05': 'mgr_t05', '06': 'mgr_t06', '07': 'mgr_t07', '08': 'mgr_t08',
  '09': 'mgr_t09', '10': 'mgr_t10', '11': 'mgr_t11', '12': 'mgr_t12',
  '13': 'mgr_t13',
};

const DEFAULT_LIASSE_TABLEAUX = [
  { id: 'tableau_01', numero: '01', type: 'auto', titleKey: 'tableau_01_title', descKey: 'tableau_01_desc', status: 'completed', items: [{ labelKey: 'demo_item_raw_materials', valeurBrute: 1250000, amortissements: 0, valeurNette: 1250000 }, { labelKey: 'demo_item_finished_products', valeurBrute: 890000, amortissements: 0, valeurNette: 890000 }] },
  { id: 'tableau_02', numero: '02', type: 'auto', titleKey: 'tableau_02_title', descKey: 'tableau_02_desc', status: 'completed', items: [{ labelKey: 'demo_item_stored_production', valeurBrute: 560000, amortissements: 0, valeurNette: 560000 }] },
  { id: 'tableau_03', numero: '03', type: 'auto', titleKey: 'tableau_03_title', descKey: 'tableau_03_desc', status: 'in_progress', items: [{ labelKey: 'demo_item_gross_salaries', dotationN: 4200000, cumulN: 4200000, taux: 100 }] },
  { id: 'tableau_04', numero: '04', type: 'auto', titleKey: 'tableau_04_title', descKey: 'tableau_04_desc', status: 'in_progress', items: [{ labelKey: 'demo_item_provisions', ouverture: 120000, dotations: 45000, reprises: 15000, cloture: 150000 }] },
  { id: 'tableau_05', numero: '05', type: 'auto', titleKey: 'tableau_05_title', descKey: 'tableau_05_desc', status: 'completed', items: [{ labelKey: 'demo_item_tangible_assets', montant: 385000 }] },
  { id: 'tableau_06', numero: '06', type: 'auto', titleKey: 'tableau_06_title', descKey: 'tableau_06_desc', status: 'pending', items: [{ labelKey: 'demo_item_industrial_equipment', montant: 1250000 }] },
  { id: 'tableau_07', numero: '07', type: 'manual', titleKey: 'tableau_07_title', descKey: 'tableau_07_desc', status: 'pending', items: [] },
  { id: 'tableau_08', numero: '08', type: 'manual', titleKey: 'tableau_08_title', descKey: 'tableau_08_desc', status: 'pending', items: [] },
  { id: 'tableau_09', numero: '09', type: 'manual', titleKey: 'tableau_09_title', descKey: 'tableau_09_desc', status: 'in_progress', items: [{ labelKey: 'demo_item_accounting_result', montant: 2850000 }] },
  { id: 'tableau_10', numero: '10', type: 'manual', titleKey: 'tableau_10_title', descKey: 'tableau_10_desc', status: 'pending', items: [] },
  { id: 'tableau_11', numero: '11', type: 'manual', titleKey: 'tableau_11_title', descKey: 'tableau_11_desc', status: 'pending', items: [] },
  { id: 'tableau_12', numero: '12', type: 'auto', titleKey: 'tableau_12_title', descKey: 'tableau_12_desc', status: 'completed', items: [{ labelKey: 'demo_item_lawyer_fees', montant: 185000 }] },
  { id: 'tableau_13', numero: '13', type: 'manual', titleKey: 'tableau_13_title', descKey: 'tableau_13_desc', status: 'pending', items: [] },
];

const DEFAULT_LIASSE_REDRESSEMENTS = [
  { id: 'r1', compte: '625100', labelKey: 'demo_red_gifts', reintegration: 45000, deduction: 0, explicationKey: 'demo_red_exp_gifts' },
  { id: 'r2', compte: '631200', labelKey: 'demo_red_fines', reintegration: 12000, deduction: 0, explicationKey: 'demo_red_exp_fines' },
  { id: 'r3', compte: '781500', labelKey: 'demo_red_vehicle_gain', reintegration: 0, deduction: 85000, explicationKey: 'demo_red_exp_vehicle' },
  { id: 'r4', compte: '681500', labelKey: 'demo_red_litigation_provision', reintegration: 95000, deduction: 0, explicationKey: 'demo_red_exp_litigation' },
];

const getTableauTitle = (tableau, p) => p(tableau.titleKey || TABLEAU_TITLE_KEYS[tableau.numero] || 'modal_tableau_default');
const getTableauDesc = (tableau, p) => p(tableau.descKey || TABLEAU_DESC_KEYS[tableau.numero] || 'modal_tableau_default');
const getItemLabel = (item, p) => (item.labelKey ? p(item.labelKey) : (item.designation || ''));
const getStatusLabel = (status, p) => p(STATUS_LABEL_KEYS[status] || status);

const buildDefaultLiasseOverview = () => {
  const year = String(new Date().getFullYear());
  return {
    exercice: year,
    dateDepot: '',
    statusKey: 'overview_status_preparation',
    completionRate: 31,
    dateDebut: `01/01/${year}`,
  };
};

const DEFAULT_LIASSE_DATA = {
  overview: buildDefaultLiasseOverview(),
  tableaux: DEFAULT_LIASSE_TABLEAUX,
  redressements: DEFAULT_LIASSE_REDRESSEMENTS,
};

const formatAmount = (n, locale) => {
  const loc = locale || getBrandLocale('ar');
  const formatted = new Intl.NumberFormat(loc, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
  return formatted;
};
const formatMoneyCell = (n, locale) => `${formatAmount(n, locale)} ${getBrandCurrencySuffix()}`;

const computeCompletionRate = (tableaux) => {
  if (!tableaux?.length) return 0;
  const done = tableaux.filter((t) => t.status === 'completed').length;
  return Math.round((done / tableaux.length) * 100);
};

const mergeLiasseData = (apiData) => {
  if (!apiData || (!apiData.tableaux?.length && !apiData.redressements?.length)) {
    return DEFAULT_LIASSE_DATA;
  }
  const tableaux = apiData.tableaux?.length ? apiData.tableaux : DEFAULT_LIASSE_TABLEAUX;
  const redressements = apiData.redressements?.length ? apiData.redressements : DEFAULT_LIASSE_REDRESSEMENTS;
  return {
    overview: {
      ...DEFAULT_LIASSE_DATA.overview,
      ...(apiData.overview || {}),
      statusKey: normalizeOverviewStatusKey({
        ...DEFAULT_LIASSE_DATA.overview,
        ...(apiData.overview || {}),
      }),
      completionRate: computeCompletionRate(tableaux),
    },
    tableaux,
    redressements,
  };
};

const DEMO_MANAGER_STATUS = {
  ACTIF: 'calculated', PASSIF: 'calculated', TCR: 'calculated', TVCP: 'pending',
  ...Object.fromEntries(DEFAULT_LIASSE_TABLEAUX.map((t) => [
    t.numero,
    t.status === 'completed' ? 'calculated' : t.status === 'in_progress' ? 'pending' : 'empty',
  ])),
  '07': 'empty', '08': 'empty', '09': 'pending', '10': 'empty', '11': 'empty', '13': 'empty',
};

const LiasseModalShell = ({ open, onClose, title, Icon, iconClass = 'text-blue-400', children, footer, maxWidth = '960px', zIndex = 99999 }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center p-4" style={{ zIndex }} onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden"
        style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-6 border-b border-white/10 shrink-0">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            {Icon && <Icon className={`h-6 w-6 ${iconClass}`} />}
            {title}
          </h2>
          <button type="button" onClick={onClose} className="text-white/70 hover:text-white transition-colors">
            <X size={24} />
          </button>
        </div>
        <div className="overflow-y-auto p-6 flex-1">{children}</div>
        {footer && <div className="p-4 border-t border-white/10 flex justify-end gap-2 flex-wrap shrink-0">{footer}</div>}
      </motion.div>
    </div>,
    document.body
  );
};

const CollapsibleSection = ({ icon: Icon, iconClass, title, expanded, onToggle, children, headerExtra }) => (
  <div className="bg-white/5 rounded-xl border border-white/20 overflow-hidden">
    <button type="button" onClick={onToggle} className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <Icon size={20} className={iconClass} />
        <h3 className="text-lg font-semibold text-white truncate">{title}</h3>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {headerExtra}
        {expanded ? <ChevronUp size={20} className="text-white/60" /> : <ChevronDown size={20} className="text-white/60" />}
      </div>
    </button>
    <AnimatePresence>
      {expanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
          <div className="p-4 pt-0">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const EmptyTabState = ({ icon: Icon, title, description }) => (
  <div className="text-center py-16 bg-white/5 rounded-xl border-2 border-dashed border-white/10">
    <Icon className="w-12 h-12 mx-auto mb-4 text-white/30" />
    <p className="text-white/80 font-medium">{title}</p>
    <p className="text-white/50 text-sm mt-2 max-w-md mx-auto">{description}</p>
  </div>
);

const LIASSE_STAT_CARD_COLORS = [
  'from-blue-500 to-blue-600',
  'from-green-500 to-green-600',
  'from-orange-500 to-orange-600',
  'from-red-500 to-red-600',
  'from-purple-500 to-purple-600',
];

/**
 * Tableau 09 - Détermination du Résultat Fiscal
 * Manual editor for fiscal adjustments (réintégrations & déductions)
 */
const Tableau09ResultatFiscal = ({ isOpen, onClose, fiscalYearId, initialData = {} }) => {
  const { p, t, i18n } = useLiasseP();
  const amountLocale = getBrandLocale(i18n.language);

  const defaultReintegrations = [
    { code: 'R01', labelKey: 'reint_r01', montant: 0 },
    { code: 'R02', labelKey: 'reint_r02', montant: 0 },
    { code: 'R03', labelKey: 'reint_r03', montant: 0 },
    { code: 'R04', labelKey: 'reint_r04', montant: 0 },
    { code: 'R05', labelKey: 'reint_r05', montant: 0 },
    { code: 'R06', labelKey: 'reint_r06', montant: 0 },
    { code: 'R07', labelKey: 'reint_r07', montant: 0 },
    { code: 'R08', labelKey: 'reint_r08', montant: 0 },
    { code: 'R09', labelKey: 'reint_r09', montant: 0 },
    { code: 'R10', labelKey: 'reint_r10', montant: 0 },
    { code: 'R11', labelKey: 'reint_r11', montant: 0 },
    { code: 'R12', labelKey: 'reint_r12', montant: 0 },
    { code: 'R99', labelKey: 'reint_r99', montant: 0 },
  ];

  const defaultDeductions = [
    { code: 'D01', labelKey: 'ded_d01', montant: 0 },
    { code: 'D02', labelKey: 'ded_d02', montant: 0 },
    { code: 'D03', labelKey: 'ded_d03', montant: 0 },
    { code: 'D04', labelKey: 'ded_d04', montant: 0 },
    { code: 'D05', labelKey: 'ded_d05', montant: 0 },
    { code: 'D06', labelKey: 'ded_d06', montant: 0 },
    { code: 'D07', labelKey: 'ded_d07', montant: 0 },
    { code: 'D08', labelKey: 'ded_d08', montant: 0 },
    { code: 'D99', labelKey: 'ded_d99', montant: 0 },
  ];

  const [data, setData] = useState({
    resultat_comptable: initialData.resultat_comptable || 0,
    reintegrations: initialData.reintegrations || defaultReintegrations.map((r) => ({ ...r, label: p(r.labelKey) })),
    deductions: initialData.deductions || defaultDeductions.map((d) => ({ ...d, label: p(d.labelKey) })),
  });

  if (!isOpen) return null;

  const totalReintegrations = data.reintegrations.reduce((sum, r) => sum + (parseFloat(r.montant) || 0), 0);
  const totalDeductions = data.deductions.reduce((sum, d) => sum + (parseFloat(d.montant) || 0), 0);
  const resultatFiscal = data.resultat_comptable + totalReintegrations - totalDeductions;

  const handleSave = async () => {
    try {
      await saveManualTableau('09', fiscalYearId, {
        resultat_comptable: data.resultat_comptable,
        reintegrations: data.reintegrations,
        deductions: data.deductions,
        resultat_fiscal: resultatFiscal
      });
      onClose();
    } catch (error) {
      logger.error('Error saving Tableau 09:', error);
      alert(p('save_error'));
    }
  };

  const updateReintegration = (index, field, value) => {
    const updated = [...data.reintegrations];
    updated[index][field] = value;
    setData({ ...data, reintegrations: updated });
  };

  const updateDeduction = (index, field, value) => {
    const updated = [...data.deductions];
    updated[index][field] = value;
    setData({ ...data, deductions: updated });
  };

  return (
    <LiasseModalShell
      open={isOpen}
      onClose={onClose}
      title={p('t09_title')}
      Icon={Calculator}
      iconClass="text-blue-400"
      maxWidth="1280px"
      zIndex={100001}
      footer={(
        <>
          <div className="text-sm text-white/60 mr-auto hidden md:block">
            {p('t09_formula', {
              comptable: formatAmount(data.resultat_comptable),
              reintegrations: formatAmount(totalReintegrations),
              deductions: formatAmount(totalDeductions),
            })}
          </div>
          <button type="button" onClick={onClose} className="px-5 py-2 text-white/70 hover:text-white transition-colors">{p('cancel')}</button>
          <button type="button" onClick={handleSave} className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl font-semibold hover:opacity-90 transition-all flex items-center gap-2">
            <Save size={16} />
            {p('btn_save')}
          </button>
        </>
      )}
    >
      <div className="space-y-6">
          {/* Result Summary */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/20">
              <p className="text-blue-300/80 text-sm">{p('t09_result_comptable')}</p>
              <p className="text-2xl font-bold text-blue-400">{formatMoneyCell(data.resultat_comptable, amountLocale)}</p>
            </div>
            <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
              <div className="flex items-center space-x-2 mb-1">
                <TrendingUp className="h-4 w-4 text-red-400" />
                <p className="text-red-300/80 text-sm">{p('t09_reintegrations')}</p>
              </div>
              <p className="text-2xl font-bold text-red-400">+{formatMoneyCell(totalReintegrations, amountLocale)}</p>
            </div>
            <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/20">
              <div className="flex items-center space-x-2 mb-1">
                <TrendingDown className="h-4 w-4 text-green-400" />
                <p className="text-green-300/80 text-sm">{p('t09_deductions')}</p>
              </div>
              <p className="text-2xl font-bold text-green-400">-{formatMoneyCell(totalDeductions, amountLocale)}</p>
            </div>
            <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/20">
              <p className="text-purple-300/80 text-sm font-semibold">{p('t09_result_fiscal')}</p>
              <p className={`text-2xl font-bold ${resultatFiscal >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatMoneyCell(resultatFiscal, amountLocale)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Réintégrations */}
            <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
              <div className="bg-red-500/20 border-b border-red-500/30 p-4">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-red-400" />
                  <h4 className="font-semibold text-white">{p('t09_reintegrations_add')}</h4>
                </div>
                <p className="text-red-200/70 text-xs mt-1">{p('t09_reintegrations_hint')}</p>
              </div>
              <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                {data.reintegrations.map((r, idx) => (
                  <div key={r.code} className="flex items-center space-x-2">
                    <input
                      type="number"
                      step="0.01"
                      value={r.montant}
                      onChange={(e) => updateReintegration(idx, 'montant', parseFloat(e.target.value) || 0)}
                      className="w-32 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder="0.00"
                    />
                    <input
                      type="text"
                      value={r.label}
                      onChange={(e) => updateReintegration(idx, 'label', e.target.value)}
                      className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                      placeholder={p('placeholder_label')}
                    />
                  </div>
                ))}
                <div className="pt-2 border-t border-white/10 mt-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-white/80 font-semibold">{p('t09_total_reintegrations')}</span>
                    <span className="text-red-400 font-bold font-mono">{formatMoneyCell(totalReintegrations, amountLocale)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Déductions */}
            <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
              <div className="bg-green-500/20 border-b border-green-500/30 p-4">
                <div className="flex items-center space-x-2">
                  <TrendingDown className="h-5 w-5 text-green-400" />
                  <h4 className="font-semibold text-white">{p('t09_deductions_sub')}</h4>
                </div>
                <p className="text-green-200/70 text-xs mt-1">{p('t09_deductions_hint')}</p>
              </div>
              <div className="p-4 space-y-2 max-h-96 overflow-y-auto">
                {data.deductions.map((d, idx) => (
                  <div key={d.code} className="flex items-center space-x-2">
                    <input
                      type="number"
                      step="0.01"
                      value={d.montant}
                      onChange={(e) => updateDeduction(idx, 'montant', parseFloat(e.target.value) || 0)}
                      className="w-32 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-green-500"
                      placeholder="0.00"
                    />
                    <input
                      type="text"
                      value={d.label}
                      onChange={(e) => updateDeduction(idx, 'label', e.target.value)}
                      className="flex-1 bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-green-500"
                      placeholder={p('placeholder_label')}
                    />
                  </div>
                ))}
                <div className="pt-2 border-t border-white/10 mt-2">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-white/80 font-semibold">{p('t09_total_deductions')}</span>
                    <span className="text-green-400 font-bold font-mono">{formatMoneyCell(totalDeductions, amountLocale)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
            <p className="text-blue-300 text-sm">
              <strong>{p('t09_legal_refs')}</strong> {p('t09_legal_text')}
            </p>
          </div>
      </div>
    </LiasseModalShell>
  );
};



/**
 * Generic Manual Tableau Editor
 * Used for Tableaux 07, 08, 10, 11, 13
 */
const TableauManualEditor = ({ isOpen, onClose, tableauNumber, title, columns, initialData = [], fiscalYearId }) => {
  const { p } = useLiasseP();
  const [rows, setRows] = useState(initialData.length > 0 ? initialData : [{}]);

  if (!isOpen) return null;

  const addRow = () => {
    setRows([...rows, {}]);
  };

  const removeRow = (index) => {
    setRows(rows.filter((_, i) => i !== index));
  };

  const updateCell = (rowIndex, columnKey, value) => {
    const updated = [...rows];
    updated[rowIndex][columnKey] = value;
    setRows(updated);
  };

  const handleSave = async () => {
    try {
      await saveManualTableau(tableauNumber, fiscalYearId, { rows });
      onClose();
    } catch (error) {
      logger.error(`Error saving Tableau ${tableauNumber}:`, error);
      alert(p('save_error'));
    }
  };

  const getInputType = (column) => {
    if (column.type === 'number' || column.type === 'currency' || column.type === 'percentage') {
      return 'number';
    }
    if (column.type === 'date') {
      return 'date';
    }
    return 'text';
  };

  return (
    <LiasseModalShell
      open={isOpen}
      onClose={onClose}
      title={p('manual_editor_title', { numero: tableauNumber, title })}
      Icon={Grid}
      iconClass="text-blue-400"
      maxWidth="1100px"
      zIndex={100001}
      footer={(
        <>
          <span className="text-sm text-white/60 mr-auto">{p('manual_rows_count', { count: rows.length })}</span>
          <button type="button" onClick={onClose} className="px-5 py-2 text-white/70 hover:text-white transition-colors">{p('cancel')}</button>
          <button type="button" onClick={handleSave} className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl font-semibold hover:opacity-90 transition-all flex items-center gap-2">
            <Save size={16} />
            {p('btn_save')}
          </button>
        </>
      )}
    >
      <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
        <table className="w-full">
          <thead className="bg-white/5 sticky top-0">
            <tr>
              {columns.map((col, idx) => (
                <th key={idx} className="px-4 py-3 text-left text-xs font-medium text-white/60 uppercase">
                  {col.label}
                </th>
              ))}
              <th className="px-4 py-3 text-center text-xs font-medium text-white/60 uppercase w-16">{p('col_action')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map((row, rowIdx) => (
              <tr key={rowIdx} className="hover:bg-white/5 transition-colors">
                {columns.map((col, colIdx) => (
                  <td key={colIdx} className="px-4 py-2">
                    {col.type === 'select' ? (
                      <select
                        value={row[col.key] || ''}
                        onChange={(e) => updateCell(rowIdx, col.key, e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">—</option>
                        {col.options?.map((opt, i) => (
                          <option key={i} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type={getInputType(col)}
                        step={col.type === 'currency' || col.type === 'percentage' ? '0.01' : undefined}
                        value={row[col.key] || ''}
                        onChange={(e) => updateCell(rowIdx, col.key, e.target.value)}
                        placeholder={col.placeholder}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-indigo-500"
                      />
                    )}
                  </td>
                ))}
                <td className="px-4 py-2 text-center">
                  <button type="button" onClick={() => removeRow(rowIdx)} className="text-red-400 hover:text-red-300 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={addRow}
        className="mt-4 w-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 px-4 py-3 rounded-xl font-medium hover:bg-indigo-500/30 transition-all flex items-center justify-center gap-2"
      >
        <Plus size={16} />
        {p('manual_add_row')}
      </button>
    </LiasseModalShell>
  );
};



/**
 * Liasse Fiscale SOCPA / ZATCA
 * Comprehensive UI for managing all 17 tableaux
 * Auto-calculates 10 tableaux, manual entry for 7
 */
const LiasseFiscaleManager = ({ isOpen, onClose, fiscalYearId }) => {
  const { p, t } = useLiasseP();
  const [loading, setLoading] = useState(false);
  const [tableaux, setTableaux] = useState([]);
  const [selectedTableau, setSelectedTableau] = useState(null);
  const [showTableauEditor, setShowTableauEditor] = useState(false);
  const [liasseManagerTab, setLiasseManagerTab] = useState('tableaux');
  const liasseManagerTabs = useMemo(() => ([
    { id: 'tableaux', label: p('manager_all_tableaux'), icon: LayoutGrid },
    { id: 'legende', label: p('manager_legend'), icon: Eye },
    { id: 'export', label: p('manager_print_options'), icon: Settings },
    { id: 'conformite', label: p('manager_nscf_compliance'), icon: Shield },
  ]), [p]);
  const [exportOptions, setExportOptions] = useState({
    arrondir_dinar: false,
    imprimer_centimes: true,
    imprimer_page_numbers: true
  });

  // Tableau definitions with status
  const tableauxDef = [
    { number: 'ACTIF', nameKey: 'mgr_actif', type: 'auto', icon: FileText, color: 'blue' },
    { number: 'PASSIF', nameKey: 'mgr_passif', type: 'auto', icon: FileText, color: 'blue' },
    { number: 'TCR', nameKey: 'mgr_tcr', type: 'auto', icon: Calculator, color: 'green' },
    { number: 'TVCP', nameKey: 'mgr_tvcp', type: 'manual', icon: TrendingUp, color: 'purple' },
    { number: '01', nameKey: 'mgr_t01', type: 'auto', icon: Grid, color: 'cyan' },
    { number: '02', nameKey: 'mgr_t02', type: 'auto', icon: Grid, color: 'cyan' },
    { number: '03', nameKey: 'mgr_t03', type: 'auto', icon: Grid, color: 'orange' },
    { number: '04', nameKey: 'mgr_t04', type: 'auto', icon: Grid, color: 'orange' },
    { number: '05', nameKey: 'mgr_t05', type: 'auto', icon: Calculator, color: 'purple' },
    { number: '06', nameKey: 'mgr_t06', type: 'auto', icon: Calculator, color: 'purple' },
    { number: '07', nameKey: 'mgr_t07', type: 'manual', icon: FileText, color: 'red' },
    { number: '08', nameKey: 'mgr_t08', type: 'manual', icon: FileText, color: 'red' },
    { number: '09', nameKey: 'mgr_t09', type: 'manual', icon: Calculator, color: 'yellow' },
    { number: '10', nameKey: 'mgr_t10', type: 'manual', icon: FileText, color: 'red' },
    { number: '11', nameKey: 'mgr_t11', type: 'manual', icon: FileText, color: 'red' },
    { number: '12', nameKey: 'mgr_t12', type: 'auto', icon: Grid, color: 'teal' },
    { number: '13', nameKey: 'mgr_t13', type: 'manual', icon: FileText, color: 'red' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadTableaux();
      setLiasseManagerTab('tableaux');
    }
  }, [isOpen, fiscalYearId]);

  const loadTableaux = async () => {
    setLoading(true);
    try {
      const data = await getLiasseFiscale({ fiscal_year_id: fiscalYearId });
      // Extract tableaux array from the data object
      setTableaux(data?.tableaux || []);
    } catch (error) {
      logger.error('Load tableaux error:', error);
      setTableaux([]); // Ensure tableaux is always an array
    } finally {
      setLoading(false);
    }
  };

  const getTableauStatus = (def) => {
    if (!Array.isArray(tableaux) || tableaux.length === 0) {
      const demoStatus = DEMO_MANAGER_STATUS[def.number];
      if (demoStatus === 'calculated') return def.type === 'auto' ? 'calculated' : 'filled';
      if (demoStatus === 'pending') return 'pending';
      return 'empty';
    }

    const tableau = tableaux.find((t) => t.tableau_number === def.number);
    if (!tableau) return 'empty';
    
    if (def.type === 'auto') {
      return tableau.auto_calculated ? 'calculated' : 'pending';
    } else {
      // Manual: check if data_json is filled
      try {
        const data = JSON.parse(tableau.data_json || '{}');
        return Object.keys(data).length > 0 ? 'filled' : 'empty';
      } catch {
        return 'empty';
      }
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'calculated':
        return <CheckCircle size={16} className="text-green-400" />;
      case 'filled':
        return <CheckCircle size={16} className="text-blue-400" />;
      case 'pending':
        return <Clock size={16} className="text-yellow-400" />;
      case 'empty':
        return <AlertTriangle size={16} className="text-red-400" />;
      default:
        return null;
    }
  };

  const openTableauEditor = (def) => {
    setSelectedTableau(def);
    setShowTableauEditor(true);
  };

  if (!isOpen) return null;

  return (
    <>
      <LiasseModalShell
        open={isOpen}
        onClose={onClose}
        title={p('manager_title')}
        Icon={FileText}
        iconClass="text-blue-400"
        maxWidth="1400px"
        footer={(
          <button type="button" onClick={onClose} className="px-6 py-2.5 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/15 transition-all">
            {t('common.close')}
          </button>
        )}
      >
        <div className="space-y-4">
          <div className="shrink-0 border-b border-white/10 pb-0">
            <div className="flex flex-wrap gap-1" role="tablist">
              {liasseManagerTabs.map((tab) => {
                const TabIcon = tab.icon;
                const active = liasseManagerTab === tab.id;
                return (
                  <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setLiasseManagerTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                    <TabIcon size={16} />
                    <span>{tab.label}</span>
                    {tab.id === 'tableaux' && loading ? <RefreshCw size={14} className="animate-spin text-white/50" /> : null}
                  </button>
                );
              })}
            </div>
          </div>

          {liasseManagerTab === 'tableaux' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 max-h-[380px] overflow-y-auto pr-1">
              {tableauxDef.map((def) => {
                const status = getTableauStatus(def);
                const Icon = def.icon;
                const isCalculated = status === 'calculated' || status === 'filled';
                const glow = isCalculated ? 'from-green-500 to-emerald-600'
                  : status === 'pending' ? 'from-orange-500 to-amber-600'
                    : def.type === 'manual' ? 'from-yellow-500 to-orange-600'
                      : 'from-blue-500 to-cyan-600';
                return (
                  <div key={def.number} className="relative group">
                    <div className={`absolute -inset-0.5 bg-gradient-to-r ${glow} rounded-2xl opacity-20 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
                    <button
                      type="button"
                      onClick={() => openTableauEditor(def)}
                      className={`relative w-full text-left rounded-2xl border p-3 transition-all hover:border-white/40 ${
                        isCalculated ? 'bg-green-500/10 border-green-500/30'
                          : def.type === 'manual' ? 'bg-yellow-500/10 border-yellow-500/30'
                            : 'bg-white/10 border-white/20'
                      }`}
                    >
                    <div className="flex items-start justify-between mb-2">
                      <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                        <Icon size={18} className="text-white/80" />
                      </div>
                      {getStatusBadge(status)}
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-white/50 text-xs font-mono">#{def.number}</span>
                      <span className={`px-2 py-0.5 text-[10px] rounded-full ${def.type === 'auto' ? 'bg-cyan-500/20 text-cyan-300' : 'bg-amber-500/20 text-amber-300'}`}>
                        {def.type === 'auto' ? p('type_auto') : p('type_manual')}
                      </span>
                    </div>
                    <h4 className="text-white font-semibold text-sm leading-tight">{p(def.nameKey || MGR_TABLEAU_NAME_KEYS[def.number] || 'modal_tableau_default')}</h4>
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {liasseManagerTab === 'legende' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {[
                { icon: CheckCircle, color: 'text-green-400', labelKey: 'legend_calculated' },
                { icon: Clock, color: 'text-yellow-400', labelKey: 'legend_pending' },
                { icon: AlertTriangle, color: 'text-red-400', labelKey: 'legend_empty' },
              ].map(({ icon: Icon, color, labelKey }) => (
                <div key={labelKey} className="flex items-center gap-2 text-white/80">
                  <Icon size={16} className={color} />
                  <span>{p(labelKey)}</span>
                </div>
              ))}
            </div>
          )}

          {liasseManagerTab === 'export' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { key: 'arrondir_dinar', labelKey: 'print_round_dinar' },
                { key: 'imprimer_centimes', labelKey: 'print_cents' },
                { key: 'imprimer_page_numbers', labelKey: 'print_page_numbers' },
              ].map(({ key, labelKey }) => (
                <label key={key} className="flex items-center space-x-3 cursor-pointer bg-white/5 rounded-xl p-3 border border-white/10">
                  <input
                    type="checkbox"
                    checked={exportOptions[key]}
                    onChange={(e) => setExportOptions({ ...exportOptions, [key]: e.target.checked })}
                    className="w-4 h-4 rounded border-white/20 bg-white/10 text-red-500"
                  />
                  <span className="text-white/80 text-sm">{p(labelKey)}</span>
                </label>
              ))}
            </div>
          )}

          {liasseManagerTab === 'conformite' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <p className="text-white/70 text-sm">
                  {p('manager_nscf_hint')}
                </p>
              </div>
            </div>
          )}
        </div>
      </LiasseModalShell>

      <AnimatePresence>
        {showTableauEditor && selectedTableau && selectedTableau.number === '09' && (
          <Tableau09ResultatFiscal
            isOpen={showTableauEditor}
            onClose={() => {
              setShowTableauEditor(false);
              loadTableaux();
            }}
            fiscalYearId={fiscalYearId}
            initialData={{}}
          />
        )}

        {showTableauEditor && selectedTableau && selectedTableau.type === 'manual' && selectedTableau.number !== '09' && (
          <TableauManualEditor
            isOpen={showTableauEditor}
            onClose={() => {
              setShowTableauEditor(false);
              loadTableaux();
            }}
            tableauNumber={selectedTableau.number}
            title={p(MGR_TABLEAU_NAME_KEYS[selectedTableau.number] || 'modal_tableau_default')}
            columns={getTableauColumns(selectedTableau.number, p)}
            initialData={[]}
          />
        )}
      </AnimatePresence>
    </>
  );
};

// Define columns for each manual tableau
function getTableauColumns(tableauNumber, p) {
  switch (tableauNumber) {
    case '07': // Cessions
      return [
        { key: 'nature', label: p('col_nature_immo'), type: 'text', placeholder: p('ph_vehicle') },
        { key: 'date_cession', label: p('col_cession_date'), type: 'date' },
        { key: 'prix_cession', label: p('col_cession_price'), type: 'currency', placeholder: '0.00' },
        { key: 'vnc', label: p('col_vnc'), type: 'currency', placeholder: '0.00' },
        { key: 'plus_moins_value', label: p('col_plus_minus'), type: 'currency', placeholder: '0.00' }
      ];
    case '08': // Provisions
      return [
        { key: 'nature', label: p('col_provision_nature'), type: 'text', placeholder: p('ph_provision_stock') },
        { key: 'debut', label: p('col_start_exercise'), type: 'currency', placeholder: '0.00' },
        { key: 'dotations', label: p('col_dotations'), type: 'currency', placeholder: '0.00' },
        { key: 'utilisees', label: p('col_used'), type: 'currency', placeholder: '0.00' },
        { key: 'reprises', label: p('col_reprises'), type: 'currency', placeholder: '0.00' },
        { key: 'fin', label: p('col_end_exercise'), type: 'currency', placeholder: '0.00' }
      ];
    case '10': // Affectation
      return [
        { key: 'designation', label: p('col_designation'), type: 'text', placeholder: p('ph_legal_reserve') },
        { key: 'montant', label: p('col_amount'), type: 'currency', placeholder: '0.00' }
      ];
    case '11': // Participations
      return [
        { key: 'societe', label: p('col_company'), type: 'text', placeholder: p('ph_company_name') },
        { key: 'capitaux_propres', label: p('col_equity'), type: 'currency', placeholder: '0.00' },
        { key: 'capital', label: p('col_capital'), type: 'currency', placeholder: '0.00' },
        { key: 'participation_pct', label: p('col_participation_pct'), type: 'percentage', placeholder: '0.00' },
        { key: 'resultat', label: p('col_result'), type: 'currency', placeholder: '0.00' },
        { key: 'dividendes', label: p('col_dividends'), type: 'currency', placeholder: '0.00' }
      ];
    case '13': // Saudi tax summary (ZATCA)
      return [
        { key: 'designation', label: p('col_designation'), type: 'text', placeholder: p('ph_tax_type') },
        { key: 'base_imposable', label: p('col_taxable_base'), type: 'currency', placeholder: '0.00' },
        { key: 'taux', label: p('col_rate'), type: 'text', placeholder: '15%' },
        { key: 'montant_du', label: p('col_tax_due'), type: 'currency', placeholder: '0.00' }
      ];
    default:
      return [
        { key: 'label', label: p('col_label'), type: 'text' },
        { key: 'value', label: p('col_amount'), type: 'currency' }
      ];
  }
}





const LiasseFiscalePage = () => {
  const { p, t, i18n } = useLiasseP();
  const [fiscalYearId, setFiscalYearId] = useState('');
  const fiscalYearOptions = useComptabiliteFiscalYearOptions();

  const [liasseFiscaleData, setLiasseFiscaleData] = useState(DEFAULT_LIASSE_DATA);
  const [activeTab, setActiveTab] = useState('tableaux');
  const [tableauFilter, setTableauFilter] = useState('all');
  const [selectedTableau, setSelectedTableau] = useState('tableau_01');
  const [showTableauModal, setShowTableauModal] = useState(false);
  const [selectedTableauData, setSelectedTableauData] = useState(null);
  const [showLiasseFiscaleManager, setShowLiasseFiscaleManager] = useState(false);
  const [showTVCPManager, setShowTVCPManager] = useState(false);
  const [showLiasseTeledeClarer, setShowLiasseTeledeClarer] = useState(false);
  const [showGenererPDFComplet, setShowGenererPDFComplet] = useState(false);
  const [showExportExcelLiasse, setShowExportExcelLiasse] = useState(false);
  const [showImportDonnees, setShowImportDonnees] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [tableauModalTab, setTableauModalTab] = useState('detail');
  const [pdfModalTab, setPdfModalTab] = useState('tableaux');
  const [excelModalTab, setExcelModalTab] = useState('contenu');
  const [importModalTab, setImportModalTab] = useState('source');
  const [teleModalTab, setTeleModalTab] = useState('exercice');
  const tableauModalTabs = useMemo(() => ([
    { id: 'detail', label: p('section_info'), icon: FileText },
    { id: 'data', label: p('section_data'), icon: Grid },
    { id: 'export', label: p('section_export'), icon: Download },
    { id: 'options', label: p('section_options'), icon: Settings },
  ]), [p]);
  const teleModalTabs = useMemo(() => ([
    { id: 'exercice', label: p('tele_exercice_section'), icon: Calendar },
    { id: 'verification', label: p('tele_verification_section'), icon: CheckCircle },
    { id: 'auth', label: p('tele_auth_section'), icon: Shield },
    { id: 'transmission', label: p('tele_transmission_section'), icon: Send },
  ]), [p]);
  const pdfModalTabs = useMemo(() => ([
    { id: 'tableaux', label: p('pdf_tableaux_section'), icon: Grid },
    { id: 'options', label: p('pdf_options_section'), icon: Settings },
    { id: 'preview', label: p('pdf_preview_section'), icon: Eye },
    { id: 'format', label: p('pdf_format_section'), icon: FileText },
  ]), [p]);
  const excelModalTabs = useMemo(() => ([
    { id: 'contenu', label: p('excel_content_section'), icon: FileSpreadsheet },
    { id: 'format', label: p('excel_format_section'), icon: Settings },
    { id: 'options', label: p('excel_options_section'), icon: Settings },
    { id: 'destination', label: p('excel_destination_section'), icon: Download },
  ]), [p]);
  const importModalTabs = useMemo(() => ([
    { id: 'source', label: p('import_source_section'), icon: Settings },
    { id: 'fichier', label: p('import_file_section'), icon: Upload },
    { id: 'validation', label: p('import_validation_section'), icon: CheckCircle },
    { id: 'journal', label: p('import_journal_section'), icon: FileText },
  ]), [p]);
  const [teledeclExercice, setTeledeclExercice] = useState('');
  const [excelFormat, setExcelFormat] = useState('');
  const [importType, setImportType] = useState('');
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('general');
  const [quickFilter, setQuickFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);

  const loadLiasseData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const liasse = await getLiasseFiscale(
        fiscalYearId ? { fiscal_year_id: fiscalYearId } : null,
      );
      setLiasseFiscaleData(mergeLiasseData(liasse));
    } catch (error) {
      logger.error('Error loading liasse fiscale:', error);
      setLoadError(p('load_error'));
      setLiasseFiscaleData(DEFAULT_LIASSE_DATA);
    }
    setIsLoading(false);
  }, [fiscalYearId, p]);

  useEffect(() => {
    getCurrentFiscalYear()
      .then((current) => {
        if (current?.id) setFiscalYearId(current.id);
        if (current?.year_name) setTeledeclExercice(String(current.year_name));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadLiasseData();
  }, [loadLiasseData]);

  useEffect(() => { if (showTableauModal) setTableauModalTab('detail'); }, [showTableauModal]);
  useEffect(() => { if (showLiasseTeledeClarer) setTeleModalTab('exercice'); }, [showLiasseTeledeClarer]);
  useEffect(() => { if (showGenererPDFComplet) setPdfModalTab('tableaux'); }, [showGenererPDFComplet]);
  useEffect(() => { if (showExportExcelLiasse) setExcelModalTab('contenu'); }, [showExportExcelLiasse]);
  useEffect(() => { if (showImportDonnees) setImportModalTab('source'); }, [showImportDonnees]);

  const renderModalTabBar = (tabs, activeTab, setActiveTab) => (
    <div className="shrink-0 border-b border-white/10 pb-0 mb-4">
      <div className="flex flex-wrap gap-1" role="tablist">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} type="button" role="tab" aria-selected={active} onClick={() => setActiveTab(tab.id)} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-t-xl text-sm font-semibold transition-colors ${active ? 'bg-white/10 text-white border-b-2 border-blue-400 -mb-px' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
              <TabIcon size={16} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const overview = liasseFiscaleData?.overview || DEFAULT_LIASSE_DATA.overview;
  const tableaux = liasseFiscaleData?.tableaux || DEFAULT_LIASSE_TABLEAUX;
  const redressements = liasseFiscaleData?.redressements || DEFAULT_LIASSE_REDRESSEMENTS;

  const filteredTableaux = useMemo(() => {
    if (tableauFilter === 'all') return tableaux;
    if (tableauFilter === 'auto') return tableaux.filter((t) => t.type === 'auto');
    if (tableauFilter === 'manual') return tableaux.filter((t) => t.type === 'manual');
    return tableaux.filter((t) => t.status === tableauFilter);
  }, [tableaux, tableauFilter]);

  const totalsRedressements = useMemo(() => ({
    reintegration: redressements.reduce((s, r) => s + (r.reintegration || 0), 0),
    deduction: redressements.reduce((s, r) => s + (r.deduction || 0), 0),
  }), [redressements]);

  const completedCount = tableaux.filter((t) => t.status === 'completed').length;
  const inProgressCount = tableaux.filter((t) => t.status === 'in_progress').length;
  const pendingCount = tableaux.filter((t) => t.status === 'pending').length;

  const liasseStats = useMemo(() => ({
    total: tableaux.length,
    completed: completedCount,
    inProgress: inProgressCount,
    pending: pendingCount,
    progression: overview.completionRate,
  }), [tableaux.length, completedCount, inProgressCount, pendingCount, overview.completionRate]);

  const statsCards = [
    { titleKey: 'stat_annexes', value: tableaux.length, icon: FileCheck, color: LIASSE_STAT_CARD_COLORS[0] },
    { titleKey: 'stat_completed', value: completedCount, icon: CheckCircle, color: LIASSE_STAT_CARD_COLORS[1] },
    { titleKey: 'stat_in_progress', value: inProgressCount, icon: RefreshCw, color: LIASSE_STAT_CARD_COLORS[2] },
    { titleKey: 'stat_pending', value: pendingCount, icon: Clock, color: LIASSE_STAT_CARD_COLORS[3] },
    { titleKey: 'stat_progression', value: `${overview.completionRate}%`, icon: TrendingUp, color: LIASSE_STAT_CARD_COLORS[4] },
  ];

  const autoCount = tableaux.filter((t) => t.type === 'auto').length;
  const manualCount = tableaux.filter((t) => t.type === 'manual').length;

  const distributionInfo = useMemo(() => {
    if (mainViewMode === 'by_type') {
      return {
        titleKey: 'dist_by_type',
        data: [
          { nameKey: 'dist_auto', count: autoCount, filterId: 'auto', color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
          { nameKey: 'dist_manual', count: manualCount, filterId: 'manual', color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
        ],
      };
    }
    if (mainViewMode === 'by_status') {
      return {
        titleKey: 'dist_by_status',
        data: [
          { nameKey: 'dist_completed', count: completedCount, filterId: 'completed', color: 'bg-gradient-to-r from-green-500 to-green-600' },
          { nameKey: 'stat_in_progress', count: inProgressCount, filterId: 'in_progress', color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
          { nameKey: 'stat_pending', count: pendingCount, filterId: 'pending', color: 'bg-gradient-to-r from-red-500 to-red-600' },
        ],
      };
    }
    return {
      titleKey: 'dist_overview',
      data: [
        { nameKey: 'dist_completed', count: completedCount, filterId: 'completed', color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { nameKey: 'stat_in_progress', count: inProgressCount, filterId: 'in_progress', color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { nameKey: 'stat_pending', count: pendingCount, filterId: 'pending', color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { nameKey: 'dist_auto', count: autoCount, filterId: 'auto', color: 'bg-gradient-to-r from-blue-500 to-blue-600' },
        { nameKey: 'dist_manual', count: manualCount, filterId: 'manual', color: 'bg-gradient-to-r from-purple-500 to-purple-600' },
      ],
    };
  }, [mainViewMode, autoCount, manualCount, completedCount, inProgressCount, pendingCount]);

  const handleAdvancedMainTab = (tabId) => {
    setMainViewMode(tabId);
    setQuickFilter('all');
    setActiveDistributionFilter(null);
    setTableauFilter('all');
  };

  const handleQuickFilter = (filterId) => {
    setQuickFilter(filterId);
    setTableauFilter(filterId);
    setActiveDistributionFilter(null);
  };

  const handleDistributionFilter = (item) => {
    const isActive = activeDistributionFilter === item.filterId;
    setActiveDistributionFilter(isActive ? null : item.filterId);
    setTableauFilter(isActive ? 'all' : item.filterId);
    setQuickFilter(isActive ? 'all' : item.filterId);
  };

  const openTableau = (tableau) => {
    setSelectedTableau(tableau.id);
    setSelectedTableauData(tableau);
    setTableauModalTab('detail');
    setShowTableauModal(true);
  };

  const handleExportPDF = async () => {
    const rows = tableaux.map((t) => ({
      [p('pdf_col_number')]: t.numero,
      [p('pdf_col_title')]: getTableauTitle(t, p),
      [p('pdf_col_type')]: t.type === 'auto' ? p('pdf_type_auto') : p('pdf_type_manual'),
      [p('pdf_col_status')]: getStatusLabel(t.status, p),
      [p('pdf_col_lines')]: t.items?.length || 0,
    }));
    await generateReportPDF(p('pdf_export_title', { exercice: overview.exercice }), rows, { orientation: 'landscape' });
    setShowGenererPDFComplet(false);
  };

  const handleExportExcel = async () => {
    try {
      const response = await exportLiasse(fiscalYearId, 'excel', { format: excelFormat });
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `liasse_fiscale_${overview.exercice}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      setShowExportExcelLiasse(false);
    } catch (error) {
      logger.error('Export Excel error:', error);
      const rows = tableaux.flatMap((t) => (t.items?.length ? t.items : [{ labelKey: t.titleKey }]).map((item) => ({
        [p('pdf_col_tableau')]: t.numero,
        [p('pdf_col_label')]: getItemLabel(item, p) || getTableauTitle(t, p),
        Montant: item.montant || item.valeurNette || item.valeurBrute || 0,
      })));
      await generateReportPDF(p('export_liasse_title', { exercice: overview.exercice }), rows, { orientation: 'landscape' });
      setShowExportExcelLiasse(false);
    }
  };

  const renderTableauCard = (tableau) => (
    <div key={tableau.id} className="relative group">
      <div className={`absolute -inset-0.5 bg-gradient-to-r ${
        tableau.status === 'completed' ? 'from-green-500 to-emerald-600'
          : tableau.status === 'in_progress' ? 'from-orange-500 to-amber-600'
            : 'from-blue-500 to-cyan-600'
      } rounded-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
      <button
        type="button"
        onClick={() => openTableau(tableau)}
        className={`relative w-full text-left bg-white/10 backdrop-blur-lg rounded-3xl border p-5 transition-all duration-300 hover:border-white/40 ${
          selectedTableau === tableau.id ? 'border-blue-400/60 ring-2 ring-blue-500/30' : 'border-white/20'
        }`}
      >
        <div className="flex items-start justify-between mb-3 gap-2">
          <div className="flex items-center space-x-2">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm ${
              tableau.status === 'completed' ? 'bg-green-500' : tableau.status === 'in_progress' ? 'bg-orange-500' : 'bg-gray-500'
            }`}>
              {tableau.numero}
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              tableau.type === 'auto' ? 'bg-cyan-500/20 text-cyan-300' : 'bg-amber-500/20 text-amber-300'
            }`}>
              {tableau.type === 'auto' ? p('type_auto') : p('type_manual')}
            </span>
          </div>
          <span className="text-xs text-white/50">{p('lines_count', { count: tableau.items?.length || 0 })}</span>
        </div>
        <h4 className="text-white font-semibold text-sm mb-1 leading-tight">{getTableauTitle(tableau, p)}</h4>
        <p className="text-white/60 text-xs leading-relaxed line-clamp-2">{getTableauDesc(tableau, p)}</p>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
          <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
            tableau.status === 'completed' ? 'bg-green-500/20 text-green-300'
              : tableau.status === 'in_progress' ? 'bg-orange-500/20 text-orange-300'
                : 'bg-blue-500/20 text-blue-300'
          }`}>
            {getStatusLabel(tableau.status, p)}
          </span>
          <span className="text-blue-400 text-sm font-medium">{p('open_arrow')}</span>
        </div>
      </button>
    </div>
  );

  const renderTableauDetailTable = () => {
    if (!selectedTableauData) return null;
    const id = selectedTableauData.id || '';
    return (
      <div className="glass-table-scroll overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10 bg-white/5">
              {id.includes('tableau_01') || id.includes('tableau_02') ? (
                <>
                  <th className="px-4 py-3 text-left text-white/80 text-sm">{p('col_designation')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_gross_value')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_amortizations')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_net_value')}</th>
                </>
              ) : id.includes('tableau_03') ? (
                <>
                  <th className="px-4 py-3 text-left text-white/80 text-sm">{p('col_designation')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_dotation_n')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_cumul_n')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_rate')}</th>
                </>
              ) : id.includes('tableau_04') ? (
                <>
                  <th className="px-4 py-3 text-left text-white/80 text-sm">{p('col_designation')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_opening')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_dotations')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_reprises')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_closing')}</th>
                </>
              ) : (
                <>
                  <th className="px-4 py-3 text-left text-white/80 text-sm">{p('col_designation')}</th>
                  <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_amount')}</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {(selectedTableauData.items || []).length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-white/50 text-sm">
                  {p('empty_table_rows')}
                </td>
              </tr>
            ) : (selectedTableauData.items || []).map((item, index) => (
              <tr key={index} className="border-b border-white/5 hover:bg-white/5">
                <td className="px-4 py-3 text-white font-medium">{getItemLabel(item, p)}</td>
                {id.includes('tableau_01') || id.includes('tableau_02') ? (
                  <>
                    <td className="px-4 py-3 text-right text-white">{formatMoneyCell(item.valeurBrute)}</td>
                    <td className="px-4 py-3 text-right text-orange-400">{formatMoneyCell(item.amortissements)}</td>
                    <td className="px-4 py-3 text-right text-emerald-400 font-bold">{formatMoneyCell(item.valeurNette)}</td>
                  </>
                ) : id.includes('tableau_03') ? (
                  <>
                    <td className="px-4 py-3 text-right text-blue-400">{formatMoneyCell(item.dotationN)}</td>
                    <td className="px-4 py-3 text-right text-orange-400">{formatMoneyCell(item.cumulN)}</td>
                    <td className="px-4 py-3 text-right text-cyan-400">{item.taux}%</td>
                  </>
                ) : id.includes('tableau_04') ? (
                  <>
                    <td className="px-4 py-3 text-right text-white">{formatMoneyCell(item.ouverture)}</td>
                    <td className="px-4 py-3 text-right text-red-400">{formatMoneyCell(item.dotations)}</td>
                    <td className="px-4 py-3 text-right text-green-400">{formatMoneyCell(item.reprises)}</td>
                    <td className="px-4 py-3 text-right text-emerald-400 font-bold">{formatMoneyCell(item.cloture)}</td>
                  </>
                ) : (
                  <td className="px-4 py-3 text-right text-emerald-400 font-bold">{formatMoneyCell(item.montant || item.valeur || 0)}</td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderTabContent = () => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500" />
        </div>
      );
    }

    if (activeTab === 'tableaux') {
      return (
        <div className="space-y-6">
          {loadError && (
            <div className="flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-xl p-3 text-orange-200 text-sm">
              <AlertCircle size={16} />
              {loadError}
            </div>
          )}
          {filteredTableaux.length === 0 ? (
            <EmptyTabState icon={LayoutGrid} title={p('empty_tableaux_title')} description={p('empty_tableaux_desc')} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {filteredTableaux.map(renderTableauCard)}
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'redressements') {
      return (
        <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600/20 to-blue-700/20 p-4 border-b border-white/10 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-r from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <AlertTriangle className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{p('redressements_title')}</h3>
              <p className="text-white/60 text-sm">{p('redressements_subtitle')}</p>
            </div>
          </div>
          {redressements.length === 0 ? (
            <EmptyTabState icon={AlertTriangle} title={p('empty_redressements_title')} description={p('empty_redressements_desc')} />
          ) : (
            <div className="p-4 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="px-4 py-3 text-left text-white/80 text-sm">{p('col_account')}</th>
                    <th className="px-4 py-3 text-left text-white/80 text-sm">{p('col_label')}</th>
                    <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_reintegration')}</th>
                    <th className="px-4 py-3 text-right text-white/80 text-sm">{p('col_deduction')}</th>
                    <th className="px-4 py-3 text-left text-white/80 text-sm">{p('col_explanation')}</th>
                  </tr>
                </thead>
                <tbody>
                  {redressements.map((r) => (
                    <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="px-4 py-3 text-cyan-400 font-mono text-sm">{r.compte}</td>
                      <td className="px-4 py-3 text-white text-sm">{r.labelKey ? p(r.labelKey) : r.libelle}</td>
                      <td className="px-4 py-3 text-right text-red-400 font-semibold">
                        {r.reintegration ? `${formatMoneyCell(r.reintegration)}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-right text-green-400 font-semibold">
                        {r.deduction ? `${formatMoneyCell(r.deduction)}` : '—'}
                      </td>
                      <td className="px-4 py-3 text-white/70 text-xs">{r.explicationKey ? p(r.explicationKey) : r.explication}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-white/20 bg-white/5">
                    <td colSpan={2} className="px-4 py-3 text-white font-semibold">{p('totals')}</td>
                    <td className="px-4 py-3 text-right text-red-400 font-bold">{formatMoneyCell(totalsRedressements.reintegration)}</td>
                    <td className="px-4 py-3 text-right text-green-400 font-bold">{formatMoneyCell(totalsRedressements.deduction)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      );
    }

    if (activeTab === 'synthese') {
      return (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { titleKey: 'synth_reintegrations', value: totalsRedressements.reintegration, subKey: 'synth_reintegrations_sub', color: 'from-red-500 to-pink-600', icon: TrendingUp },
              { titleKey: 'synth_deductions', value: totalsRedressements.deduction, subKey: 'synth_deductions_sub', color: 'from-green-500 to-emerald-600', icon: TrendingDown },
              { titleKey: 'synth_completeness', value: overview.completionRate, subKey: 'synth_tableaux_count', subOpts: { completed: completedCount, total: tableaux.length }, color: 'from-orange-500 to-red-600', icon: FileCheck, suffix: '%' },
            ].map((card, i) => (
              <div key={i} className="relative group">
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${card.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} style={{ pointerEvents: 'none' }} />
                <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className={`p-2 rounded-xl bg-gradient-to-r ${card.color}`}>
                      <card.icon className="h-5 w-5 text-white" />
                    </div>
                    <h4 className="text-white/80 text-sm font-semibold">{p(card.titleKey)}</h4>
                  </div>
                  <p className="text-2xl font-bold text-white">
                    {card.suffix ? `${card.value}${card.suffix}` : `${formatMoneyCell(card.value, getBrandLocale(i18n.language))}`}
                  </p>
                  <p className="text-white/50 text-sm mt-1">{card.subOpts ? p(card.subKey, card.subOpts) : p(card.subKey)}</p>
                </div>
              </div>
            ))}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-4">{p('quick_actions')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { labelKey: 'action_pdf', icon: FileSpreadsheet, className: 'bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg shadow-blue-500/30 text-white', action: () => { setPdfModalTab('tableaux'); setShowGenererPDFComplet(true); } },
                { labelKey: 'action_excel', icon: Download, className: 'bg-green-500/20 text-green-200 border border-green-500/30 hover:bg-green-500/30', action: () => { setExcelModalTab('contenu'); setShowExportExcelLiasse(true); } },
                { labelKey: 'action_import', icon: Upload, className: 'bg-orange-500/20 text-orange-200 border border-orange-500/30 hover:bg-orange-500/30', action: () => { setImportModalTab('source'); setShowImportDonnees(true); } },
                { labelKey: 'action_teledeclare', icon: Send, className: 'bg-gradient-to-r from-blue-500 to-blue-700 shadow-lg shadow-blue-500/30 text-white', action: () => { setTeleModalTab('exercice'); setShowLiasseTeledeClarer(true); } },
              ].map((btn, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={btn.action}
                  className={`${btn.className} p-4 rounded-xl font-medium hover:opacity-90 transition-all flex items-center space-x-3`}
                >
                  <btn.icon className="h-5 w-5 shrink-0" />
                  <span className="text-sm">{p(btn.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const PDF_TABLEAUX = ['ACTIF', 'PASSIF', 'TCR', 'TVCP', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12', '13'];

  return (
    <>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
          <div>
            <h2 className="text-xl font-semibold text-white">{p('title')}</h2>
            <p className="text-white/60 text-sm">
              {p('subtitle', { exercice: overview.exercice })}
            </p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-2.5 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}
            >
              <Filter className="h-5 w-5" />
              <span className="hidden sm:inline">{p('advanced_options')}</span>
              {showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={() => setShowLiasseFiscaleManager(true)}
              className="bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-5 py-2 rounded-xl font-semibold transition-all flex items-center space-x-2 shadow-lg shadow-blue-500/30"
            >
              <FileText size={16} />
              <span>{p('manage_liasse')}</span>
            </button>
            <button
              type="button"
              onClick={() => setShowTVCPManager(true)}
              className="bg-white/10 backdrop-blur-lg border border-white/20 text-white px-5 py-2 rounded-xl font-medium hover:bg-white/15 transition-all flex items-center space-x-2"
            >
              <TrendingUp size={16} />
              <span>{p('tvcp')}</span>
            </button>
            <button
              type="button"
              onClick={() => { setTeleModalTab('exercice'); setShowLiasseTeledeClarer(true); }}
              className="bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center space-x-2 shadow-lg shadow-blue-500/30"
            >
              <Send size={16} />
              <span>{p('teledeclare')}</span>
            </button>
          </div>
        </div>

        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {statsCards.slice(0, 4).map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
                <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`} />
                <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white/60 text-sm font-medium">{p(stat.titleKey)}</p>
                      <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                    </div>
                    <div className="relative">
                      <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`} />
                      <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                        <stat.icon className="h-6 w-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div initial={false} animate={{ height: showAdvancedOptions ? 'auto' : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: 'easeInOut' }} style={{ overflow: 'hidden' }}>
            <div className="space-y-4 pt-2">
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                  {ADVANCED_MAIN_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => handleAdvancedMainTab(tab.id)}
                      className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap min-w-[120px] ${
                        mainViewMode === tab.id
                          ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                          : 'text-white/80 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <tab.icon className="h-5 w-5" />
                      <span className="hidden sm:inline">{p(tab.nameKey)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {mainViewMode === 'general' && (
                <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
                  <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
                    {QUICK_FILTER_BUTTONS.map((filter) => (
                      <button
                        key={filter.id}
                        type="button"
                        onClick={() => handleQuickFilter(filter.id)}
                        className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap min-w-[100px] ${
                          quickFilter === filter.id
                            ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                            : 'text-white/80 hover:bg-white/10 hover:text-white'
                        }`}
                      >
                        <filter.icon className="h-5 w-5" />
                        <span className="hidden sm:inline">{p(filter.nameKey)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                  <h3 className="text-xl font-semibold text-white">{p(distributionInfo.titleKey)}</h3>
                  <div className="flex items-center gap-3">
                    <span className="text-white/60 text-sm">{p('global_progress')}</span>
                    <span className="text-white font-semibold">{overview.completionRate}%</span>
                    {activeDistributionFilter && (
                      <button
                        type="button"
                        onClick={() => { setActiveDistributionFilter(null); setTableauFilter('all'); setQuickFilter('all'); }}
                        className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                      >
                        <XCircle size={14} />
                        {p('reset_filter')}
                      </button>
                    )}
                  </div>
                </div>
                <div className="w-full bg-white/10 rounded-full h-2 mb-5 overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${overview.completionRate}%` }}
                    transition={{ duration: 0.8 }}
                    className="bg-gradient-to-r from-blue-500 to-blue-700 h-full rounded-full"
                  />
                </div>
                <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '4')}>
                  {distributionInfo.data.map((item) => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    const pct = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                    return (
                      <button
                        key={item.filterId}
                        type="button"
                        onClick={() => handleDistributionFilter(item)}
                        className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left w-full ${
                          activeDistributionFilter === item.filterId
                            ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20'
                            : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <h4 className={`font-medium ${activeDistributionFilter === item.filterId ? 'text-white' : 'text-white/80'}`}>{p(item.nameKey)}</h4>
                          <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white font-semibold`}>{item.count}</div>
                        </div>
                        <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${pct}%` }}
                            transition={{ duration: 0.6 }}
                            className={`h-3 rounded-full ${item.color}`}
                          />
                        </div>
                        <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterId ? 'text-white/80' : 'text-white/60'}`}>{p('percent_of_total', { percent: pct })}</p>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          </motion.div>

          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto hide-scrollbar">
              {MAIN_TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 whitespace-nowrap min-w-[140px] ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                      : 'text-white/80 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{p(tab.labelKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {renderTabContent()}
        </div>
      </div>

      {showTableauModal && selectedTableauData ? (
      <LiasseModalShell
        open
        onClose={() => setShowTableauModal(false)}
        title={selectedTableauData ? p('modal_tableau_title', { numero: selectedTableauData.numero, titre: getTableauTitle(selectedTableauData, p) }) : p('modal_tableau_default')}
        Icon={Grid}
        iconClass="text-blue-400"
        maxWidth="1100px"
        footer={(
          <>
            <button type="button" onClick={() => setShowTableauModal(false)} className="px-5 py-2 text-white/70 hover:text-white transition-colors">{t('common.close')}</button>
            <button type="button" onClick={() => { setShowTableauModal(false); setShowLiasseFiscaleManager(true); }} className="px-5 py-2 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/15 transition-all">{p('btn_edit')}</button>
            <button type="button" onClick={handleExportPDF} className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl font-medium hover:opacity-90 transition-all">{p('btn_export_pdf')}</button>
          </>
        )}
      >
        <div className="space-y-4">
          {renderModalTabBar(tableauModalTabs, tableauModalTab, setTableauModalTab)}
          {tableauModalTab === 'detail' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <p className="text-white/70 text-sm">{selectedTableauData ? getTableauDesc(selectedTableauData, p) : ''}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className={`text-xs px-3 py-1 rounded-full font-semibold ${
                    selectedTableauData?.status === 'completed' ? 'bg-green-500/20 text-green-300'
                      : selectedTableauData?.status === 'in_progress' ? 'bg-orange-500/20 text-orange-300'
                        : 'bg-blue-500/20 text-blue-300'
                  }`}>
                    {getStatusLabel(selectedTableauData?.status, p)}
                  </span>
                  <span className="text-xs px-3 py-1 rounded-full bg-white/10 text-white/70">
                    {selectedTableauData?.type === 'auto' ? p('type_auto_full') : p('type_manual_full')}
                  </span>
                </div>
              </div>
            </div>
          )}
          {tableauModalTab === 'data' && renderTableauDetailTable()}
          {tableauModalTab === 'export' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <p className="text-white/70 text-sm">{p('export_tableau_hint')}</p>
                <button type="button" onClick={handleExportPDF} className="mt-3 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl text-sm font-medium shadow-lg shadow-blue-500/30">{p('btn_export_pdf')}</button>
              </div>
            </div>
          )}
          {tableauModalTab === 'options' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center space-x-3 text-white text-sm md:col-span-3">
                <input type="checkbox" defaultChecked className="rounded" />
                <span>{p('option_include_totals')}</span>
              </label>
            </div>
          )}
        </div>
      </LiasseModalShell>
      ) : null}

      <LiasseModalShell
        open={showLiasseTeledeClarer}
        onClose={() => setShowLiasseTeledeClarer(false)}
        title={p('tele_modal_title')}
        Icon={Send}
        iconClass="text-blue-400"
        maxWidth="900px"
        footer={(
          <>
            <button type="button" onClick={() => setShowLiasseTeledeClarer(false)} className="px-5 py-2 text-white/70 hover:text-white transition-colors">{t('common.close')}</button>
            <button type="button" className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl font-semibold hover:opacity-90 transition-all flex items-center gap-2 shadow-lg shadow-blue-500/30">
              <Send size={18} />
              {p('tele_submit')}
            </button>
          </>
        )}
      >
        <div className="space-y-4">
          {renderModalTabBar(teleModalTabs, teleModalTab, setTeleModalTab)}
          {teleModalTab === 'exercice' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3 bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-blue-200 text-sm">
                {p('tele_verify_before')}
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('tele_fiscal_year')}</label>
                <CustomSelect
                  value={teledeclExercice}
                  onChange={setTeledeclExercice}
                  options={fiscalYearOptions.length
                    ? fiscalYearOptions
                    : [{ value: String(new Date().getFullYear()), label: String(new Date().getFullYear()) }]}
                  placeholder={p('tele_choose_exercice')}
                  searchable
                  creatable
                  autoSelect={false}
                />
              </div>
              <div className="md:col-span-2 flex items-end">
                <p className="text-white/50 text-xs">{p('tele_deadline', { date: overview.dateDepot || '—' })}</p>
              </div>
            </div>
          )}
          {teleModalTab === 'verification' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['tele_check_validated', 'tele_check_signatures', 'tele_check_archive'].map((itemKey) => (
                <label key={itemKey} className="flex items-center space-x-3 text-white text-sm">
                  <input type="checkbox" className="rounded" />
                  <span>{p(itemKey)}</span>
                </label>
              ))}
            </div>
          )}
          {teleModalTab === 'auth' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block w-full text-white/70 text-sm mb-1.5 font-medium text-right">{p('tele_dgi_login')}</label>
                <input type="text" placeholder="300000000000003" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-1.5 font-medium text-right">{p('tele_password')}</label>
                <input type="password" className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
              </div>
              <div>
                <label className="block w-full text-white/70 text-sm mb-1.5 font-medium text-right">{p('tele_otp')}</label>
                <input type="text" placeholder={p('tele_otp_ph')} maxLength={6} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
              </div>
            </div>
          )}
          {teleModalTab === 'transmission' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <p className="text-white/70 text-sm">{p('tele_transmission_hint')}</p>
              </div>
            </div>
          )}
        </div>
      </LiasseModalShell>

      <LiasseModalShell
        open={showGenererPDFComplet}
        onClose={() => setShowGenererPDFComplet(false)}
        title={p('pdf_modal_title')}
        Icon={FileSpreadsheet}
        iconClass="text-blue-400"
        footer={(
          <button type="button" onClick={handleExportPDF} className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl font-semibold hover:opacity-90 transition-all flex items-center gap-2">
            <FileSpreadsheet size={18} />
            {p('pdf_generate')}
          </button>
        )}
      >
        <div className="space-y-4">
          {renderModalTabBar(pdfModalTabs, pdfModalTab, setPdfModalTab)}
          {pdfModalTab === 'tableaux' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
              {PDF_TABLEAUX.map((tab) => (
                <label key={tab} className="flex items-center space-x-2 text-white text-sm">
                  <input type="checkbox" defaultChecked className="rounded" />
                  <span>{p('pdf_tableau_item', { num: tab })}</span>
                </label>
              ))}
            </div>
          )}
          {pdfModalTab === 'options' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['pdf_cover_page', 'pdf_page_numbers', 'pdf_round_dinar'].map((optKey, i) => (
                <label key={optKey} className="flex items-center space-x-3 text-white text-sm">
                  <input type="checkbox" defaultChecked={i < 2} className="rounded" />
                  <span>{p(optKey)}</span>
                </label>
              ))}
            </div>
          )}
          {pdfModalTab === 'preview' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <p className="text-white/70 text-sm">{p('pdf_preview_hint', { count: tableaux.length })}</p>
              </div>
            </div>
          )}
          {pdfModalTab === 'format' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <p className="text-white/70 text-sm">{p('pdf_format_hint')}</p>
              </div>
            </div>
          )}
        </div>
      </LiasseModalShell>

      <LiasseModalShell
        open={showExportExcelLiasse}
        onClose={() => setShowExportExcelLiasse(false)}
        title={p('excel_modal_title')}
        Icon={Download}
        iconClass="text-blue-400"
        footer={(
          <button type="button" onClick={handleExportExcel} className="px-6 py-2 bg-green-500/20 border border-green-500/30 text-green-200 rounded-xl font-semibold hover:bg-green-500/30 transition-all flex items-center gap-2">
            <Download size={18} />
            {p('excel_download')}
          </button>
        )}
      >
        <div className="space-y-4">
          {renderModalTabBar(excelModalTabs, excelModalTab, setExcelModalTab)}
          {excelModalTab === 'contenu' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['excel_all_tableaux', 'excel_cover', 'excel_redressements', 'excel_metadata'].map((itemKey) => (
                <label key={itemKey} className="flex items-center space-x-3 text-white text-sm">
                  <input type="checkbox" defaultChecked className="rounded" />
                  <span>{p(itemKey)}</span>
                </label>
              ))}
            </div>
          )}
          {excelModalTab === 'format' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('excel_workbook_structure')}</label>
                <CustomSelect
                  value={excelFormat}
                  onChange={setExcelFormat}
                  options={[
                    { value: 'single', label: p('excel_format_single') },
                    { value: 'sheets', label: p('excel_format_sheets') },
                    { value: 'external', label: p('excel_format_external') },
                  ]}
                  placeholder={p('excel_choose_format')}
                  searchable
                  creatable
                  autoSelect={false}
                />
              </div>
            </div>
          )}
          {excelModalTab === 'options' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['excel_option_formulas', 'excel_option_lock', 'excel_option_metadata'].map((optKey) => (
                <label key={optKey} className="flex items-center space-x-3 text-white text-sm">
                  <input type="checkbox" className="rounded" />
                  <span>{p(optKey)}</span>
                </label>
              ))}
            </div>
          )}
          {excelModalTab === 'destination' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <p className="text-white/70 text-sm">{p('excel_destination_hint')}</p>
              </div>
            </div>
          )}
        </div>
      </LiasseModalShell>

      <LiasseModalShell
        open={showImportDonnees}
        onClose={() => setShowImportDonnees(false)}
        title={p('import_modal_title')}
        Icon={Upload}
        iconClass="text-blue-400"
        maxWidth="720px"
        footer={(
          <button type="button" className="px-6 py-2 bg-orange-500/20 border border-orange-500/30 text-orange-200 rounded-xl font-semibold hover:bg-orange-500/30 transition-all flex items-center gap-2">
            <Upload size={18} />
            {p('import_start')}
          </button>
        )}
      >
        <div className="space-y-4">
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-blue-200 text-sm">
            {p('import_warning')}
          </div>
          {renderModalTabBar(importModalTabs, importModalTab, setImportModalTab)}
          {importModalTab === 'source' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('import_type_label')}</label>
                <CustomSelect
                  value={importType}
                  onChange={setImportType}
                  options={[
                    { value: 'qawaem', label: p('import_type_siterp') },
                    { value: 'txt', label: p('import_type_txt') },
                    { value: 'csv', label: p('import_type_csv') },
                  ]}
                  placeholder={p('import_choose_type')}
                  searchable
                  creatable
                  autoSelect={false}
                />
              </div>
            </div>
          )}
          {importModalTab === 'fichier' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3 border-2 border-dashed border-white/20 rounded-xl p-8 text-center">
                <Upload size={40} className="mx-auto mb-3 text-white/40" />
                <p className="text-white font-medium mb-2">{p('import_drop_hint')}</p>
                <button type="button" className="bg-white/10 hover:bg-white/20 text-white px-5 py-2 rounded-lg transition-colors text-sm mt-2">{p('import_browse')}</button>
                <p className="text-white/40 text-xs mt-3">{p('import_formats')}</p>
              </div>
            </div>
          )}
          {importModalTab === 'validation' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="flex items-center space-x-3 text-white text-sm md:col-span-3">
                <input type="checkbox" className="rounded" />
                <span>{p('import_check_totals')}</span>
              </label>
            </div>
          )}
          {importModalTab === 'journal' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <p className="text-white/70 text-sm">{p('import_journal_hint')}</p>
              </div>
            </div>
          )}
        </div>
      </LiasseModalShell>

      <LiasseFiscaleManager
        isOpen={showLiasseFiscaleManager}
        onClose={() => { setShowLiasseFiscaleManager(false); loadLiasseData(); }}
        fiscalYearId={fiscalYearId}
      />


      <TVCPManager
        isOpen={showTVCPManager}
        onClose={() => setShowTVCPManager(false)}
        fiscalYearId={fiscalYearId}
      />
    </>
  );
};
export default LiasseFiscalePage;
export { LiasseFiscaleManager };

